/**
 * New Menu API Response Types
 * Updated to handle breaking changes with partial success support
 */

export interface MenusAPIResponse {
  success: StoreMenuResult[];     // Successfully retrieved menus
  failed: StoreError[];           // Failed store requests with details
  metadata: ResponseMetadata;     // Response metadata and statistics
  count: number;                  // Total requested stores
  page: number;                   // Current page
  pages: number;                  // Total pages
}

export interface StoreMenuResult {
  storeNumber: string;           // Store number
  menuData: MenuData;           // Menu data
  menuType: "active" | "inactive";
  retrievedAt: string;          // ISO timestamp
}

export interface StoreError {
  storeNumber: string;          // Store that failed
  errorCode: string;            // Standardized error code
  errorType: string;            // Error category
  message: string;              // Human-readable message
  details?: string;             // Technical details
  timestamp: string;            // ISO timestamp
}

export interface ResponseMetadata {
  totalRequested: number;       // Total stores requested
  successCount: number;         // Successfully retrieved
  failureCount: number;         // Failed retrievals
  partialSuccess: boolean;      // Whether partial success occurred
  processingTime: string;       // Processing duration
  failuresByType: {             // Count of failures by type
    [errorType: string]: number;
  };
}

// MenuData interface (keeping existing structure for backward compatibility)
export interface MenuData {
  products?: any[];
  bundles?: any[];
  categories?: any[];
  [key: string]: any;
}

// Error type constants
export enum MenuAPIErrorType {
  STORE_NOT_FOUND = 'STORE_NOT_FOUND',
  MENU_NOT_AVAILABLE = 'MENU_NOT_AVAILABLE',
  MENU_FETCH_FAILED = 'MENU_FETCH_FAILED',
  MENU_PARSE_ERROR = 'MENU_PARSE_ERROR',
  CACHE_ERROR = 'CACHE_ERROR',
  SYSTEM_ERROR = 'SYSTEM_ERROR'
}

// Error code constants
export enum MenuAPIErrorCode {
  STORE_404 = 'STORE_404',
  MENU_404 = 'MENU_404',
  MENU_FETCH_500 = 'MENU_FETCH_500',
  MENU_PARSE_400 = 'MENU_PARSE_400',
  CACHE_500 = 'CACHE_500',
  SYSTEM_500 = 'SYSTEM_500'
}

// HTTP Status codes for menu API
export enum MenuAPIStatus {
  SUCCESS = 200,           // All stores successful
  PARTIAL_SUCCESS = 207,   // Partial success
  BAD_REQUEST = 400,       // Invalid request
  SERVER_ERROR = 500       // Complete failure
}

// Legacy response structure for backward compatibility
export interface LegacyMenuResponse {
  menus: any[];
  count: number;
  page: number;
  pages: number;
}

// Union type for handling both old and new responses during migration
export type MenuResponse = MenusAPIResponse | LegacyMenuResponse;

// Type guard to check if response is new format
export const isNewMenuResponse = (response: MenuResponse): response is MenusAPIResponse => {
  return 'success' in response && 'failed' in response && 'metadata' in response;
};

// Type guard to check if response is legacy format
export const isLegacyMenuResponse = (response: MenuResponse): response is LegacyMenuResponse => {
  return 'menus' in response && !('success' in response);
};